import { FC } from 'react';
import { Button, Card, CardContent, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CloseIcon from '@mui/icons-material/Close';
import { SmallerChip } from '@/components/Tag/styles';
import type { TagGroupSuggestion } from '../types';

interface MergeSuggestionCardProps {
  suggestion: TagGroupSuggestion;
  tagCounts: Record<string, number>;
  onAccept: () => void;
  onDismiss: () => void;
  isMerging: boolean;
  isMobile: boolean;
}

const MergeSuggestionCard: FC<MergeSuggestionCardProps> = ({
  suggestion,
  tagCounts,
  onAccept,
  onDismiss,
  isMerging,
  isMobile,
}) => {
  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Stack spacing={1.5}>
          {/* Source tags */}
          <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
            {suggestion.source_tags.map((tag) => (
              <SmallerChip
                key={tag}
                label={`${tag} (${tagCounts[tag] ?? 0})`}
                sx={{
                  height: isMobile ? '28px' : '24px',
                  '& .MuiChip-label': {
                    fontSize: isMobile ? '11px' : '12px',
                    padding: isMobile ? '4px 6px' : '4px 8px',
                  },
                }}
              />
            ))}
          </Stack>

          {/* Arrow + target */}
          <Stack direction="row" alignItems="center" gap={1}>
            <MergeTypeIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={700}>
              {suggestion.target_tag}
            </Typography>
          </Stack>

          {/* AI reason */}
          <Typography variant="body2" color="text.secondary">
            {suggestion.reason}
          </Typography>

          {/* Actions */}
          <Stack direction="row" alignItems="center" gap={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={onAccept}
              disabled={isMerging}
              startIcon={isMerging ? <CircularProgress size={16} /> : undefined}
            >
              {isMerging ? 'Merging...' : 'Accept'}
            </Button>
            <IconButton size="small" onClick={onDismiss} disabled={isMerging} aria-label="Dismiss suggestion">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MergeSuggestionCard;
